// notifications-page.component.ts - FIXED VERSION
import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationItem, NotificationService } from '../services/notification.service';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '../shared/auth/auth.service';
import { Router } from '@angular/router';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service'; // Add this import

type FilterTab = 'all' | 'unread';

@Component({
  selector: 'app-notifications-page',
  templateUrl: './notifications-page.component.html',
  styleUrls: ['./notifications-page.component.scss'],
  imports: [MatIconModule, MatProgressSpinnerModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsPageComponent implements OnInit, OnDestroy {
  notifications: NotificationItem[] = [];
  isLoading = false;
  isLoadingMore = false;
  hasMore = true;
  activeTab: FilterTab = 'all';
  totalCount = 0;
  unreadCount = 0;
  currentRole = '';

  private skip = 0;
  private readonly PAGE_SIZE = 20;
  private destroy$ = new Subject<void>();
  private auth = inject(AuthService);
  

  constructor(
    private notifService: NotificationService,
    private cdr: ChangeDetectorRef,
     private router: Router,
       private encounterService: EncounterServiceService,
  ) {}

  ngOnInit(): void {
    // ✅ Subscribe to user role changes
    this.auth.user
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: any) => {
        if (user?.role) {
          this.currentRole = user.role;
          // ✅ Reload notifications when role changes
          this.loadNotifications(true);
        }
      });

    // Subscribe to unread count changes
    this.notifService.unreadCount
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadCount = count;
        this.cdr.markForCheck();
      });

    // Subscribe to notifications list updates from service
    this.notifService.notifications
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.notifications = items;
        this.cdr.markForCheck();
      });

    // Subscribe to new real-time notifications
    this.notifService.newNotification
      .pipe(takeUntil(this.destroy$))
      .subscribe((newNotif) => {
        console.log('📬 Real-time notification received in page:', newNotif);
        // ✅ Reload total count for current role
        this.reloadTotalCount();
      });
  }

  /**
   * ✅ FIXED: Reload total count using role-based endpoint
   */
  private reloadTotalCount(): void {
    const currentUser = this.auth.user.getValue();
    if (!currentUser?.role) return;

    this.notifService.loadRecentNotificationsByRole(currentUser.role, 1, 0)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.totalCount = res.count;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to reload total count:', err);
        }
      });
  }

  /**
   * Load notifications for current role
   */
  loadNotifications(reset = false): void {
    const currentUser = this.auth.user.getValue();
    if (!currentUser?.role) return;

    if (reset) {
      this.skip = 0;
      this.hasMore = true;
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }

    this.notifService.loadRecentNotificationsByRole(currentUser.role, this.PAGE_SIZE, this.skip)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          console.log('📋 Notifications loaded:', res.data);
          this.totalCount = res.count;
          this.hasMore = res.data.length === this.PAGE_SIZE;
          this.skip += res.data.length;
          this.isLoading = false;
          this.isLoadingMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.isLoadingMore = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Filter notifications based on active tab
   */
  get filtered(): NotificationItem[] {
    if (this.activeTab === 'unread') {
      return this.notifications.filter(n => !n.isRead);
    }
    return this.notifications;
  }

  /**
   * Get count for "All" tab
   */
  get allCount(): number {
    return this.notifications.length;
  }

  setTab(tab: FilterTab): void {
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

onNotifClick(notif: NotificationItem): void {
  this.onMarkAsRead(notif);

  // Navigate to events page with the specific event ID as a query param
  if (notif.triggeredByResourceType === 'Event' && notif.triggeredByResourceId) {
    this.router.navigate(['/events'], {
      queryParams: { eventId: notif.triggeredByResourceId }
    });
  }
  
  if (notif.triggeredByResourceType === 'ServiceRequest' && notif.triggeredByResourceId) {
    this.router.navigate(['/app/service-requests'], {
      queryParams: { serviceRequestId: notif.triggeredByResourceId }
    });
  }
}





  onMarkAsRead(notif: NotificationItem): void {
    if (notif.isRead) return;
    this.notifService.markAsRead(notif.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  onMarkAllRead(): void {
    this.notifService.markAllAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  onDelete(event: Event, notifId: string): void {
    event.stopPropagation();
    this.notifService.deleteNotification(notifId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.totalCount -= 1;
          this.cdr.markForCheck();
        }
      });
  }

  loadMore(): void {
    if (this.isLoadingMore || !this.hasMore) return;
    this.loadNotifications(false);
  }

  getNotifIcon(type: number): string {
    const icons: Record<number, string> = {
      1: 'science', 2: 'biotech', 3: 'check_circle',
      10: 'medication', 11: 'local_pharmacy', 12: 'verified',
      20: 'event', 21: 'event_busy', 22: 'update', 23: 'alarm',
      30: 'celebration', 31: 'edit_calendar', 32: 'cancel',
      40: 'build', 41: 'task_alt', 42: 'done_all',
      50: 'how_to_reg', 51: 'person_off', 52: 'archive', 53: 'restore',
      100: 'warning'
    };
    return icons[type] ?? 'notifications';
  }

  getNotifColor(type: number): string {
    if (type >= 1 && type <= 3) return 'lab';
    if (type >= 10 && type <= 12) return 'med';
    if (type >= 20 && type <= 23) return 'apt';
    if (type >= 30 && type <= 32) return 'evt';
    if (type >= 40 && type <= 42) return 'svc';
    if (type >= 50 && type <= 53) return 'usr';
    return 'sys';
  }

  getTypeLabel(type: number): string {
    if (type >= 1 && type <= 3) return 'Lab';
    if (type >= 10 && type <= 12) return 'Medication';
    if (type >= 20 && type <= 23) return 'Appointment';
    if (type >= 30 && type <= 32) return 'Event';
    if (type >= 40 && type <= 42) return 'Service';
    if (type >= 50 && type <= 53) return 'Account';
    return 'System';
  }

  formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  formatFullDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  }

  trackById(_: number, item: NotificationItem): string {
    return item.id;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}